import { Button, Dropdown, Layer } from '@carbon/react';
import MonthPicker from 'components/month-picker/month-picker';
import { memo, useMemo, useEffect, useRef } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { handleIntegerKeyPress, isLocked } from '../utils';
import { FieldState, getError } from 'atoms/form-atoms';
import { MineralType } from 'types/minerals';
import { VolumeModelFormAtoms, RampUpModel } from '../types';
import style from './volume-model.module.scss';
import classNames from 'classnames';
import { useConvertToHyperbolic, useDcaVolumeHotkeys } from '../hooks';
import {
  isOilModelValidAtom,
  isGasModelValidAtom,
  oilModelFormValuesAtom,
  gasModelFormValuesAtom,
  isMainDCAModelNonExponentialAtom,
  isWaterModelValidAtom,
  isPrimaryDCAModelValidAtom,
  lockParamsAtom,
} from '../atoms';
import { Locked, Unlocked } from '@carbon/react/icons';
import { LockableInput } from './lockable-input';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';

export interface VolumeModelProps {
  streamType: MineralType;
  formAtoms: VolumeModelFormAtoms;
  versionId: string;
  uwi: string;
}

function VolumeModelImpl({
  streamType,
  formAtoms,
  versionId,
  uwi,
}: VolumeModelProps) {
  const [form, setForm] = useAtom(formAtoms);
  const [declineModelField, setDeclineModelField] = useAtom(
    form.declineModelAtomField
  );

  const [lockParams, setLockParams] = useAtom(lockParamsAtom);
  const initialized = useRef(false);

  const [startDateField, setStartDateField] = useAtom(form.startDateAtomField);
  const [qiField, setQiField] = useAtom(form.qiAtomField);
  const [diField, setDiField] = useAtom(form.diAtomField);
  const isOilModelValid = useAtomValue(isOilModelValidAtom);
  const isGasModelValid = useAtomValue(isGasModelValidAtom);
  const isWaterModelValid = useAtomValue(isWaterModelValidAtom);
  const oilModelFormValues = useAtomValue(oilModelFormValuesAtom);
  const gasModelFormValues = useAtomValue(gasModelFormValuesAtom);
  const isMainDCAModelNonExponential = useAtomValue(
    isMainDCAModelNonExponentialAtom
  );
  const isPrimaryDCAModelValid = useAtomValue(isPrimaryDCAModelValidAtom);
  const isOilPrimary =
    isOilModelValid && oilModelFormValues.declineModel.value !== 'ratio';
  const isGasPrimary =
    isGasModelValid && gasModelFormValues.declineModel.value !== 'ratio';
  let isRatioAvailable: boolean;
  if (streamType === 'oil') {
    isRatioAvailable = isGasPrimary;
  } else if (streamType === 'gas') {
    isRatioAvailable = isOilPrimary;
  } else {
    isRatioAvailable = isOilPrimary || isGasPrimary;
  }
  const primaryStartDate = useMemo(() => {
    if (declineModelField.value === 'ratio') {
      if (isOilPrimary) {
        return oilModelFormValues.startDate.value;
      } else if (isGasPrimary) {
        return gasModelFormValues.startDate.value;
      }
    }
    return null;
  }, [
    declineModelField.value,
    isOilPrimary,
    isGasPrimary,
    oilModelFormValues.startDate.value,
    gasModelFormValues.startDate.value,
  ]);
  let riUnits = '';
  if (declineModelField.value === 'ratio') {
    if (isOilPrimary) {
      if (streamType === 'gas') {
        riUnits = 'mcf/bbl';
      } else if (streamType === 'water') {
        riUnits = 'bbl/bbl';
      }
    } else if (isGasPrimary) {
      if (streamType === 'oil') {
        riUnits = 'bbl/mmcf';
      } else if (streamType === 'water') {
        riUnits = 'bbl/mmcf';
      }
    }
  }
  const fallbackAtom = useMemo(
    () =>
      atom({
        value: null,
        isValid: true,
        isDirty: false,
        error: null,
      }),
    []
  );
  const [rampupModelField, setRampupModelField] = useAtom(
    form.rampupModelAtomField
  );
  const [qiRampupField, setQiRampupField] = useAtom(
    form.qiRampupAtomField ?? fallbackAtom
  );
  const [rampupDurationField, setRampupDurationField] = useAtom(
    form.rampupDurationAtomField ?? fallbackAtom
  );
  const [nField, setNField] = useAtom(form.nAtomField ?? fallbackAtom);
  const [bFactorField, setBFactorField] = useAtom(
    form.bFactorAtomField ?? fallbackAtom
  );
  const [dlimField, setDlimField] = useAtom(form.dlimAtomField ?? fallbackAtom);
  const [riField, setRiField] = useAtom(form.riAtomField ?? fallbackAtom);
  const [tElfField, setTElfField] = useAtom(form.tElfAtomField ?? fallbackAtom);
  const [mField, setMField] = useAtom(form.mAtomField ?? fallbackAtom);

  const qiFormatField = useAtomValue(form.qiFormat);
  const diFormatField = useAtomValue(form.diFormat);

  const scope = `DCA_INPUT_FORM_${
    streamType.toUpperCase() as Uppercase<MineralType>
  }` as const;

  useDcaVolumeHotkeys({
    formAtom: formAtoms,
    scope: scope,
  });

  useEffect(() => {
    if (declineModelField.value === 'ratio' && primaryStartDate) {
      setStartDateField(primaryStartDate);
    }
  }, [declineModelField.value, primaryStartDate, setStartDateField]);

  const { data, setIsConvertingToHyperbolic } = useConvertToHyperbolic(
    uwi,
    versionId,
    streamType
  );

  const productionUnit = streamType === 'gas' ? 'mcf' : 'bbl';
  const qiTimeUnit = qiFormatField.value === 'months' ? 'month' : 'day';

  const qiUnit = `${productionUnit}/${qiTimeUnit}`;
  const diUnit = diFormatField.value === 'effective' ? '%/year' : `1/month`;
  const dlimUnit = diUnit;

  const validFormAtoms = {
    oil: isOilModelValid,
    gas: isGasModelValid,
    water: isWaterModelValid,
  };

  const isConvertToHyperbolicDisabled = !(
    isMainDCAModelNonExponential &&
    declineModelField.value === 'ratio' &&
    isPrimaryDCAModelValid &&
    validFormAtoms[streamType as keyof typeof validFormAtoms]
  );

  const handleLockIconClick = (
    stream: 'oil' | 'gas' | 'water',
    type: 'dca' | 'rampUp',
    parameter: string,
    value: number | string | null
  ) => {
    if (!value) return;
    if (isLocked(lockParams, stream, type, parameter)) {
      setLockParams({
        action: 'unlock',
        stream: stream,
        type: type,
        parameter: parameter,
      });
    } else {
      setLockParams({
        action: 'lock',
        stream: stream,
        type: type,
        parameter: parameter,
        value: value,
      });
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    setLockParams({
      action: 'reset',
      stream: streamType,
      type: 'dca',
    });
  }, [declineModelField.value, setLockParams, streamType]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    setLockParams({
      action: 'reset',
      stream: streamType,
      type: 'rampUp',
    });
  }, [rampupModelField.value, setLockParams, streamType]);

  return (
    <div className={style.volumeModelContainer}>
      <div
        className={classNames(
          style.monthPicker,
          declineModelField.value === 'ratio' && style.disabledField
        )}
      >
        <MonthPicker
          icon={
            isLocked(lockParams, streamType, 'dca', 'forecastStartDate') ? (
              <Locked />
            ) : (
              <Unlocked />
            )
          }
          disabled={isLocked(
            lockParams,
            streamType,
            'dca',
            'forecastStartDate'
          )}
          onIconClick={() => {
            if (startDateField.value) {
              handleLockIconClick(
                streamType,
                'dca',
                'forecastStartDate',
                parseMomentUtc(startDateField.value).format(SERVER_DATE_FORMAT)
              );
            }
          }}
          light
          isInvalid={!startDateField.isValid && startDateField.isDirty}
          selectedMonth={
            declineModelField.value === 'ratio' && primaryStartDate
              ? parseMomentUtc(primaryStartDate)
              : startDateField.value
                ? parseMomentUtc(startDateField.value)
                : null
          }
          setSelectedMonth={(month, year) => {
            const newDate = parseMomentUtc(`${year}-01-01`)
              .month(month)
              .startOf('month')
              .toDate();
            setStartDateField(newDate);
          }}
          label="Start Date"
        />
      </div>
      <div className={classNames(style.declineModelRow, style.volumeModelGap)}>
        <div
          className={classNames(
            style.fullRowDropdown,
            declineModelField.value === 'ratio'
          )}
        >
          <Layer>
            <Dropdown
              id={`${streamType}-rampup-model-dropdown`}
              titleText="Ramp-up Model"
              label="None"
              items={['None', 'Constant', 'Linear', 'Exponential']}
              selectedItem={
                rampupModelField.value.charAt(0).toUpperCase() +
                rampupModelField.value.slice(1)
              }
              disabled={declineModelField.value === 'ratio'}
              className={
                declineModelField.value === 'ratio' ? style.disabledField : ''
              }
              onChange={(e) => {
                let newRampupModel = 'none' as RampUpModel;
                if (e.selectedItem === 'None') {
                  newRampupModel = 'none';
                } else if (e.selectedItem === 'Constant') {
                  newRampupModel = 'constant';
                } else if (e.selectedItem === 'Linear') {
                  newRampupModel = 'linear';
                } else if (e.selectedItem === 'Exponential') {
                  newRampupModel = 'exponential';
                }
                setRampupModelField(newRampupModel);
                setForm({
                  declineModel: declineModelField.value,
                  rampupModel: newRampupModel,
                  forecastStartDate: startDateField.value,
                  qi: qiField.value,
                  di: diField.value,
                  bFactor: bFactorField?.value,
                  dlim: dlimField?.value,
                  qiRampup: qiRampupField?.value,
                  rampupDuration: rampupDurationField?.value,
                  n: nField?.value,
                  ri: riField?.value,
                  tElf: tElfField?.value,
                  m: mField?.value,
                });
              }}
            />
          </Layer>
        </div>
      </div>
      {(rampupModelField.value === 'constant' ||
        rampupModelField.value === 'linear' ||
        rampupModelField.value === 'exponential') && (
        <div
          className={classNames(
            style.declineModelRow,
            style.volumeModelGap,
            style.declineModelRowNumeric
          )}
        >
          <LockableInput
            inputType="number"
            inputMode="numeric"
            isLocked={isLocked(lockParams, streamType, 'rampUp', 'qi')}
            onToggleLock={() =>
              handleLockIconClick(
                streamType,
                'rampUp',
                'qi',
                qiRampupField?.value
              )
            }
            light
            className={classNames(style.noWheel)}
            id={`${streamType}-rampup-qi-input`}
            labelText={`qi ${qiUnit}`}
            max={
              rampupModelField.value === 'linear' ? 100_000_000 : 100_000_000
            }
            min={rampupModelField.value === 'linear' ? -100_000_000 : 0}
            size="md"
            step={1}
            value={qiRampupField?.value ?? ''}
            placeholder="0"
            invalid={!qiRampupField?.isValid && qiRampupField?.isDirty}
            invalidText={
              !qiRampupField?.isValid && qiRampupField?.isDirty
                ? getError(qiRampupField as FieldState<number | null>) || ''
                : ''
            }
            onKeyDown={(e) => {
              handleIntegerKeyPress(e, rampupModelField.value === 'linear');
            }}
            onChange={(val) => {
              setQiRampupField(val as number);
            }}
          />

          <LockableInput
            inputType="number"
            isLocked={isLocked(lockParams, streamType, 'rampUp', 'tRamp')}
            onToggleLock={() =>
              handleLockIconClick(
                streamType,
                'rampUp',
                'tRamp',
                rampupDurationField?.value
              )
            }
            light
            className={classNames(style.fixedWidthNumberInput, style.noWheel)}
            id={`${streamType}-rampup-duration-input`}
            labelText="Ramp-up Duration months"
            inputMode="numeric"
            max={100_000}
            min={0}
            size="md"
            step={1}
            value={rampupDurationField?.value ?? ''}
            placeholder="0"
            invalid={
              !rampupDurationField?.isValid && rampupDurationField?.isDirty
            }
            invalidText={
              !rampupDurationField?.isValid && rampupDurationField?.isDirty
                ? getError(rampupDurationField as FieldState<number | null>) ||
                  ''
                : ''
            }
            onKeyDown={handleIntegerKeyPress}
            onChange={(val) => {
              setRampupDurationField(val as number);
            }}
          />
        </div>
      )}
      {(rampupModelField.value === 'linear' ||
        rampupModelField.value === 'exponential') && (
        <div
          className={classNames(style.declineModelRow, style.volumeModelGap)}
        >
          <LockableInput
            inputType="number"
            isLocked={isLocked(lockParams, streamType, 'rampUp', 'n')}
            onToggleLock={() =>
              handleLockIconClick(streamType, 'rampUp', 'n', nField?.value)
            }
            light
            className={style.noWheel}
            id={`${streamType}-rampup-n-input`}
            labelText={
              rampupModelField.value === 'linear'
                ? `n ${productionUnit}/month^2`
                : 'n 1/month'
            }
            inputMode="decimal"
            max={rampupModelField.value === 'linear' ? 1_000_000 : 9.9999}
            min={rampupModelField.value === 'linear' ? -1_000_000.0 : 0.0001}
            step={rampupModelField.value === 'linear' ? 1 : 0.0001}
            value={nField?.value ?? ''}
            placeholder={
              rampupModelField.value === 'linear' ? '0.00' : '0.0000'
            }
            invalid={!nField?.isValid && nField?.isDirty}
            invalidText={
              !nField?.isValid && nField?.isDirty
                ? getError(nField as FieldState<number | null>) || ''
                : ''
            }
            onChange={(val) => {
              setNField(val as number);
            }}
          />
        </div>
      )}
      <div className={classNames(style.declineModelRow, style.volumeModelGap)}>
        <div
          className={style.fullRowDropdown}
          data-ratio-disabled={!isRatioAvailable}
        >
          <Layer>
            <Dropdown
              id={`${streamType}-decline-model-dropdown`}
              titleText="DCA Model"
              label="Decline Model TODO"
              items={[
                'Modified Hyperbolic',
                'Hyperbolic',
                'Exponential',
                'Ratio',
              ]}
              selectedItem={
                declineModelField.value === 'modified_hyperbolic'
                  ? 'Modified Hyperbolic'
                  : declineModelField.value === 'hyperbolic'
                    ? 'Hyperbolic'
                    : declineModelField.value === 'exponential'
                      ? 'Exponential'
                      : declineModelField.value === 'ratio'
                        ? 'Ratio'
                        : 'Modified Hyperbolic'
              }
              onChange={(e) => {
                if (e.selectedItem === 'Ratio' && !isRatioAvailable) {
                  return;
                }
                let newDeclineModel;
                if (e.selectedItem === 'Modified Hyperbolic') {
                  newDeclineModel = 'modified_hyperbolic';
                  setDeclineModelField('modified_hyperbolic');
                } else if (e.selectedItem === 'Hyperbolic') {
                  newDeclineModel = 'hyperbolic';
                  setDeclineModelField('hyperbolic');
                } else if (e.selectedItem === 'Exponential') {
                  newDeclineModel = 'exponential';
                  setDeclineModelField('exponential');
                } else if (e.selectedItem === 'Ratio') {
                  newDeclineModel = 'ratio';
                  setDeclineModelField('ratio');
                  setRampupModelField('none');
                } else {
                  newDeclineModel = 'modified_hyperbolic';
                }
                setForm({
                  bFactor: bFactorField?.value,
                  declineModel: newDeclineModel,
                  rampupModel:
                    newDeclineModel === 'ratio'
                      ? 'none'
                      : rampupModelField.value,
                  forecastStartDate: startDateField.value,
                  qi: qiField.value,
                  di: diField.value,
                  dlim: dlimField?.value,
                  qiRampup: qiRampupField?.value,
                  rampupDuration: rampupDurationField?.value,
                  n: nField?.value,
                  ri: riField?.value,
                  tElf: tElfField?.value,
                  m: mField?.value,
                });
              }}
            />
          </Layer>
        </div>
      </div>
      {declineModelField.value !== 'ratio' && (
        <>
          <div
            className={classNames(
              style.declineModelRow,
              style.volumeModelGap,
              style.declineModelRowNumeric
            )}
          >
            <LockableInput
              inputType="number"
              isLocked={isLocked(lockParams, streamType, 'dca', 'qi')}
              onToggleLock={() =>
                handleLockIconClick(streamType, 'dca', 'qi', qiField?.value)
              }
              light
              className={classNames(style.fixedWidthNumberInput, style.noWheel)}
              id={`${streamType}-default-number-input-qi`}
              labelText={`qi ${qiUnit}`}
              inputMode="numeric"
              max={100_000_000}
              min={1}
              size="md"
              step={1}
              value={qiField.value ?? ''}
              placeholder="0"
              invalid={!qiField.isValid && qiField.isDirty}
              invalidText={
                !qiField.isValid && qiField.isDirty
                  ? getError(qiField) || ''
                  : ''
              }
              onKeyDown={handleIntegerKeyPress}
              onChange={(val) => {
                setQiField(val as number);
              }}
            />
            <LockableInput
              inputType="number"
              isLocked={isLocked(lockParams, streamType, 'dca', 'di')}
              onToggleLock={() =>
                handleLockIconClick(streamType, 'dca', 'di', diField?.value)
              }
              light
              className={style.noWheel}
              id={`${streamType}-default-number-input-di`}
              labelText={`Di ${diUnit}`}
              inputMode="decimal"
              max={diFormatField.value === 'nominal' ? 0.9999 : 99.99}
              min={diFormatField.value === 'nominal' ? 0.0001 : 0.01}
              step={0.01}
              value={diField.value ?? ''}
              placeholder="0.00"
              invalid={!diField.isValid && diField.isDirty}
              invalidText={
                !diField.isValid && diField.isDirty
                  ? getError(diField) || ''
                  : ''
              }
              onChange={(val) => {
                setDiField(val as number);
              }}
            />
          </div>

          <div
            className={classNames(
              style.declineModelRow,
              style.volumeModelGap,
              style.declineModelRowNumeric
            )}
          >
            {(declineModelField.value === 'hyperbolic' ||
              declineModelField.value === 'modified_hyperbolic') && (
              <LockableInput
                inputType="number"
                isLocked={isLocked(lockParams, streamType, 'dca', 'bFactor')}
                onToggleLock={() =>
                  handleLockIconClick(
                    streamType,
                    'dca',
                    'bFactor',
                    bFactorField?.value
                  )
                }
                light
                className={style.noWheel}
                id={`${streamType}-b-factor-input`}
                labelText="b-factor"
                inputMode="decimal"
                min={0.01}
                max={9.99}
                step={0.01}
                value={bFactorField?.value ?? ''}
                placeholder="0.00"
                invalid={!bFactorField?.isValid && bFactorField?.isDirty}
                invalidText={
                  !bFactorField?.isValid && bFactorField?.isDirty
                    ? getError(bFactorField as FieldState<number | null>) || ''
                    : ''
                }
                onChange={(val) => {
                  setBFactorField(val as number);
                }}
              />
            )}

            {declineModelField.value === 'modified_hyperbolic' && (
              <LockableInput
                inputType="number"
                isLocked={isLocked(lockParams, streamType, 'dca', 'dlim')}
                onToggleLock={() =>
                  handleLockIconClick(
                    streamType,
                    'dca',
                    'dlim',
                    dlimField?.value
                  )
                }
                light
                className={style.noWheel}
                id={`${streamType}-dlim-input`}
                labelText={`Dlim ${dlimUnit}`}
                inputMode="decimal"
                max={diFormatField.value === 'nominal' ? 0.9999 : 99.99}
                min={diFormatField.value === 'nominal' ? 0.0001 : 0.01}
                step={0.01}
                value={dlimField?.value ?? ''}
                placeholder="0.00"
                invalid={!dlimField?.isValid && dlimField?.isDirty}
                invalidText={
                  !dlimField?.isValid && dlimField?.isDirty
                    ? getError(dlimField as FieldState<number | null>) || ''
                    : ''
                }
                onChange={(val) => {
                  setDlimField(val as number);
                }}
              />
            )}
          </div>
        </>
      )}
      {declineModelField.value === 'ratio' && (
        <>
          <div
            className={classNames(
              style.declineModelRow,
              style.volumeModelGap,
              style.declineModelRowNumeric
            )}
          >
            <LockableInput
              inputType="number"
              isLocked={isLocked(lockParams, streamType, 'dca', 'ri')}
              onToggleLock={() =>
                handleLockIconClick(streamType, 'dca', 'ri', riField?.value)
              }
              light
              className={style.noWheel}
              id={`${streamType}-ri-input`}
              labelText={`Ri ${riUnits}`}
              inputMode="decimal"
              min={0.01}
              max={10000}
              step={0.01}
              value={riField?.value ?? ''}
              placeholder="0.00"
              invalid={!riField?.isValid && riField?.isDirty}
              invalidText={
                !riField?.isValid && riField?.isDirty
                  ? getError(riField as FieldState<number | null>) || ''
                  : ''
              }
              onChange={(val) => {
                setRiField(val as number);
              }}
            />
            <LockableInput
              inputType="number"
              isLocked={isLocked(lockParams, streamType, 'dca', 'tElf')}
              onToggleLock={() =>
                handleLockIconClick(streamType, 'dca', 'tElf', tElfField?.value)
              }
              light
              className={classNames(style.fixedWidthNumberInput, style.noWheel)}
              id={`${streamType}-telf-input`}
              labelText="t_elf months"
              inputMode="numeric"
              min={1}
              max={10000}
              step={1}
              value={tElfField?.value ?? ''}
              placeholder="0"
              invalid={!tElfField?.isValid && tElfField?.isDirty}
              invalidText={
                !tElfField?.isValid && tElfField?.isDirty
                  ? getError(tElfField as FieldState<number | null>) || ''
                  : ''
              }
              onKeyDown={handleIntegerKeyPress}
              onChange={(val) => {
                setTElfField(val as number);
              }}
            />
          </div>

          <div
            className={classNames(style.declineModelRow, style.volumeModelGap)}
          >
            <LockableInput
              inputType="number"
              isLocked={isLocked(lockParams, streamType, 'dca', 'm')}
              onToggleLock={() =>
                handleLockIconClick(streamType, 'dca', 'm', mField?.value)
              }
              light
              className={style.noWheel}
              id={`${streamType}-m-input`}
              labelText="m"
              inputMode="decimal"
              min={-1.0}
              max={1.0}
              step={0.01}
              value={mField?.value ?? ''}
              placeholder="0.00"
              invalid={!mField?.isValid && mField?.isDirty}
              invalidText={
                !mField?.isValid && mField?.isDirty
                  ? getError(mField as FieldState<number | null>) || ''
                  : ''
              }
              onChange={(val) => {
                setMField(val as number);
              }}
            />
          </div>
          {declineModelField.value === 'ratio' && (
            <div
              className={classNames(
                style.declineModelRow,
                style.volumeModelGap
              )}
            >
              <Button
                size="sm"
                onClick={() => setIsConvertingToHyperbolic(true)}
                disabled={isConvertToHyperbolicDisabled || data.loading}
              >
                Convert to Hyperbolic
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const VolumeModel = memo(VolumeModelImpl);
